/**
 * Tenant-API fetch wrapper. Reads the API key from localStorage on every call so a
 * rotation by the tenant during a session takes effect immediately.
 *
 * Key storage:
 *   localStorage.getItem('beamx.apiKey')  → the bearer token (full `beamx_live_...` string)
 *
 * In production the key never leaves the browser tab; we don't proxy it through the
 * portal's own cookie session. Phase 13 self-serve signup replaces this with a real
 * cookie-backed portal session that issues short-lived API tokens server-side.
 */

const STORAGE_KEY = 'beamx.apiKey';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `API error ${status}`);
    this.name = 'ApiError';
  }
}

export function getApiKey(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(STORAGE_KEY);
}

export function setApiKey(key: string | null) {
  if (typeof window === 'undefined') return;
  if (key && key.trim()) window.localStorage.setItem(STORAGE_KEY, key.trim());
  else window.localStorage.removeItem(STORAGE_KEY);
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  signal?: AbortSignal;
}

export async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
  };
  const key = getApiKey();
  if (key) headers.Authorization = `Bearer ${key}`;

  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  const payload = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) throw new ApiError(res.status, payload);
  return payload as T;
}
